/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import { AutoFocusPlugin } from '@lexical/react/LexicalAutoFocusPlugin';
import { CharacterLimitPlugin } from '@lexical/react/LexicalCharacterLimitPlugin';
import { CheckListPlugin } from '@lexical/react/LexicalCheckListPlugin';
import { ClearEditorPlugin } from '@lexical/react/LexicalClearEditorPlugin';
import LexicalClickableLinkPlugin from '@lexical/react/LexicalClickableLinkPlugin';
import { CollaborationPlugin } from '@lexical/react/LexicalCollaborationPlugin';
import LexicalErrorBoundary from '@lexical/react/LexicalErrorBoundary';
import { HashtagPlugin } from '@lexical/react/LexicalHashtagPlugin';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { HorizontalRulePlugin } from '@lexical/react/LexicalHorizontalRulePlugin';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin';
import { PlainTextPlugin } from '@lexical/react/LexicalPlainTextPlugin';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { TabIndentationPlugin } from '@lexical/react/LexicalTabIndentationPlugin';
import { TablePlugin } from '@lexical/react/LexicalTablePlugin';
import useLexicalEditable from '@lexical/react/useLexicalEditable';
import { HeadingNode, QuoteNode } from "@lexical/rich-text";
import { TableCellNode, TableNode, TableRowNode } from "@lexical/table";
import { ListItemNode, ListNode } from "@lexical/list";
import { AutoLinkNode, LinkNode } from "@lexical/link";
import { HorizontalRuleNode } from "@lexical/react/LexicalHorizontalRuleNode";
import { useEffect, useState } from 'react';
import { $getRoot, $getSelection, EditorState, Klass, LexicalEditor, LexicalNode, $createParagraphNode } from 'lexical';

import { $generateHtmlFromNodes } from '@lexical/html';
import { $generateNodesFromDOM } from '@lexical/html';
import { InitialEditorStateType, LexicalComposer } from '@lexical/react/LexicalComposer';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import ImagesPlugin from './Images';
import FloatingLinkEditorPlugin from './FloatingLinkEditor';
import RTEToolbar from './RTEToolbar';
import theme from './Theme';
import { ImageNode } from './ImageNode';
import AutoLink from './AutoLink';
// import {CAN_USE_DOM} from 'shared/canUseDOM';

// import {createWebsocketProvider} from './collaboration';
// import {useSettings} from './context/SettingsContext';
// import {useSharedHistoryContext} from './context/SharedHistoryContext';
// import TableCellNodes from './nodes/TableCellNodes';
// import ActionsPlugin from './plugins/ActionsPlugin';
// import AutocompletePlugin from './plugins/AutocompletePlugin';
// import AutoEmbedPlugin from './plugins/AutoEmbedPlugin';
// import AutoLinkPlugin from './plugins/AutoLinkPlugin';
// import CodeHighlightPlugin from './plugins/CodeHighlightPlugin';
// import CollapsiblePlugin from './plugins/CollapsiblePlugin';
// import CommentPlugin from './plugins/CommentPlugin';
// import ComponentPickerPlugin from './plugins/ComponentPickerPlugin';
// import ContextMenuPlugin from './plugins/ContextMenuPlugin';
// import DragDropPaste from './plugins/DragDropPastePlugin';
// import EmojiPickerPlugin from './plugins/EmojiPickerPlugin';
// import EmojisPlugin from './plugins/EmojisPlugin';
// import FloatingTextFormatToolbarPlugin from './plugins/FloatingTextFormatToolbarPlugin';
// import ImagesPlugin from './plugins/ImagesPlugin';
// import KeywordsPlugin from './plugins/KeywordsPlugin';
// import LinkPlugin from './plugins/LinkPlugin';
// import ListMaxIndentLevelPlugin from './plugins/ListMaxIndentLevelPlugin';
// import MarkdownShortcutPlugin from './plugins/MarkdownShortcutPlugin';
// import {MaxLengthPlugin} from './plugins/MaxLengthPlugin';
// import MentionsPlugin from './plugins/MentionsPlugin';
// import SpeechToTextPlugin from './plugins/SpeechToTextPlugin';
// import TabFocusPlugin from './plugins/TabFocusPlugin';
// import TableCellResizer from './plugins/TableCellResizer';
// import TableOfContentsPlugin from './plugins/TableOfContentsPlugin';
// import {TablePlugin as NewTablePlugin} from './plugins/TablePlugin';
// import ToolbarPlugin from './plugins/ToolbarPlugin';
// import TreeViewPlugin from './plugins/TreeViewPlugin';
// import RTE from './themes/RTE';
// import ContentEditable from './ui/ContentEditable';
// import Placeholder from './ui/Placeholder';




// Catch any errors that occur during Lexical updates and log them
// or throw them as needed. If you don't throw them, Lexical will
// try to recover gracefully without losing user data.
function onError(error: any) {
  console.error(error);
}

interface Props {
  onChange?(newVal: string): void;
  value: string;
  disabled?: boolean;
}

export default function RTE({ onChange, value, disabled }: Props) {
  const [floatingAnchorElem, setFloatingAnchorElem] = useState<HTMLDivElement | null>(null);

  const initialConfig = {
    namespace: 'RTE',
    theme,
    onError: (error: any) => console.error(error),
    nodes: [
      HeadingNode,
      ListNode,
      ListItemNode,
      QuoteNode,
      TableNode,
      TableCellNode,
      TableRowNode,
      LinkNode,
      ImageNode,
      HorizontalRuleNode,
    ],
    editorState: (editor: any) => {
      const root = $getRoot();
      const parser = new DOMParser();
      const dom = parser.parseFromString(value || '', 'text/html');
      const nodes = $generateNodesFromDOM(editor, dom);
      nodes.forEach((n) => root.append(n));
    },
  };

  const onRef = (_floatingAnchorElem: HTMLDivElement) => {
    if (_floatingAnchorElem !== null) {
      setFloatingAnchorElem(_floatingAnchorElem);
    }
  };

  const renderLinkEditor = () => {
    return floatingAnchorElem ? (
      <FloatingLinkEditorPlugin anchorElem={floatingAnchorElem as HTMLElement} />
    ) : (
      <></>
    );
  };

  // New component for managing editor instance and editable state
  function EditorStateManager() {
    const [editor] = useLexicalComposerContext();

    // When the disabled prop changes, update the editor editable state
    useEffect(() => {
      editor.setEditable(!disabled);
    }, [disabled, editor]);

    return null;
  }

  // When the editor changes, you can get notified via the LexicalOnChangePlugin
  const handleChange = (editorState: any, editor: any) => {
    editorState.read(() => {
      const htmlString = $generateHtmlFromNodes(editor);
      if (onChange) {
        onChange(htmlString);
      }
    });
  };
  console.log(value)
  return (
    <div className='editor-shell'>
      <LexicalComposer initialConfig={initialConfig}>
        <RTEToolbar />
        <RichTextPlugin
          contentEditable={
            <div className='editor-container'>
              <div className="editor-scroller">
                <div className="editor" ref={onRef} style={{ minHeight: 500 }}>
                  <ContentEditable className='ContentEditable__root' />
                </div>
              </div>
            </div>
          }
          placeholder={<div className='Placeholder__root'></div>}
          ErrorBoundary={LexicalErrorBoundary}
        />
        <OnChangePlugin onChange={handleChange} />
        {renderLinkEditor()}

        {/* Add this to dynamically toggle the editable state */}
        <EditorStateManager />
      </LexicalComposer>
    </div>
  );
}